import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { PageWrapper } from '../components/PageWrapper';
import PageHero from '../components/PageHero';

const mockGallery = {
  categories: ['all', 'exterior', 'pool', 'food', 'nature'],
  items: [
    // Pool
    { id:1, category:'pool', title:'Infinity Pool', url:'/pool1.jpeg' },
    { id:2, category:'pool', title:'Poolside Bliss', url:'/pool2.jpeg' },
    { id:3, category:'pool', title:'Aerial Pool View', url:'/img1.jpeg' },
    // Exterior
    { id:4, category:'exterior', title:'Main Villa', url:'/exterior.jpeg' },
    { id:5, category:'exterior', title:'Floral Entrance', url:'/img5.jpeg' },
    { id:6, category:'exterior', title:'Villa Gardens', url:'/img6.jpeg' },
    // Food (The 11 new images)
    { id:7, category:'food', title:'Authentic Kerala Feast', url:'/food1.jpeg' },
    { id:8, category:'food', title:'Traditional Breakfast', url:'/food2.jpeg' },
    { id:9, category:'food', title:'Spicy Delicacies', url:'/food3.jpeg' },
    { id:10, category:'food', title:'Garden Fresh Ingredients', url:'/food4.jpeg' },
    { id:11, category:'food', title:'Chef\'s Special', url:'/food5.jpeg' },
    { id:12, category:'food', title:'Sweet Treats', url:'/food6.jpeg' },
    { id:13, category:'food', title:'Healthy Options', url:'/food7.jpeg' },
    { id:14, category:'food', title:'Fruit Platter', url:'/food8.jpeg' },
    { id:15, category:'food', title:'Afternoon Tea', url:'/food9.jpeg' },
    { id:16, category:'food', title:'Gourmet Dinner', url:'/food10.jpeg' },
    { id:17, category:'food', title:'Home-style Cooking', url:'/food11.jpeg' },
    // Nature
    { id:30, category:'nature', title:'Chembra Peak', url:'/img2.jpeg' },
    { id:31, category:'nature', title:'Mountain Mist', url:'/img3.jpeg' },
    { id:32, category:'nature', title:'Forest Surroundings', url:'/img4.jpeg' },
    { id:33, category:'nature', title:'Sunset Glow', url:'/img7.jpeg' },
  ]
};

export default function Gallery() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [lightbox, setLightbox] = useState(null);

  const { data: galleryData } = useQuery({
    queryKey: ['gallery'],
    queryFn: () => axios.get('/api/gallery').then(r => r.data).catch(() => mockGallery)
  });

  const data = galleryData || mockGallery;
  const filtered = activeFilter === 'all' ? data.items : data.items.filter(i => i.category === activeFilter);

  return (
    <PageWrapper>
      <Helmet>
        <title>Visual Gallery – Villa Tesori | Photo Journey of Wayanad</title>
        <meta name="description" content="View photos of Villa Tesori — luxury suites, infinity pool, tea gardens, and Chembra Peak views. Witness the beauty of Kerala's best homestay in Wayanad." />
        <meta name="keywords" content="Wayanad homestay photos, Chembra Peak images, infinity pool Kerala homestay, luxury stay Wayanad gallery, Vellarimala pictures" />
        <meta property="og:title" content="Gallery & Visual Journey – Villa Tesori Wayanad" />
        <meta property="og:description" content="See the magic of Villa Tesori through our photo gallery. Mountains, tea gardens, and luxury stays." />
        <meta property="og:image" content="https://villatesori.com/pool1.jpeg" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://villatesori.com/gallery" />
        <link rel="canonical" href="https://villatesori.com/gallery" />
      </Helmet>

      <PageHero
        scriptText="Visual Journey"
        title="Gallery"
        subtitle="A thousand pictures, one experience — the magic of Wayanad at Villa Tesori"
        bgImage="/img5.jpeg"
      />

      <section className="section">
        <div className="container">
          {/* Filter Tabs */}
          <div className="gallery-filters" style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 56, flexWrap: 'wrap' }}>
            {data.categories.map(cat => (
              <button key={cat} onClick={() => setActiveFilter(cat)}
                style={{
                  padding: '10px 24px', border: '1px solid',
                  borderColor: activeFilter === cat ? 'var(--forest)' : 'var(--mist)',
                  background: activeFilter === cat ? 'var(--forest)' : 'transparent',
                  color: activeFilter === cat ? 'var(--gold-light)' : 'var(--text-muted)',
                  fontFamily: 'Jost, sans-serif', fontSize: '0.78rem',
                  letterSpacing: '0.14em', textTransform: 'capitalize',
                  cursor: 'pointer', transition: 'all 0.3s', borderRadius: 2,
                }}
              >{cat}</button>
            ))}
          </div>

          {/* Masonry Grid */}
          <div className="gallery-masonry" style={{ columns: 3, gap: 16, columnFill: 'balance' }}>
            <AnimatePresence>
              {filtered.map((item) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4 }}
                  style={{ marginBottom: 16, breakInside: 'avoid', cursor: 'pointer' }}
                  onClick={() => setLightbox(item)}
                >
                  <div className="img-overlay" style={{ borderRadius: 2 }}>
                    <img src={item.url} alt={item.title} style={{ width: '100%', display: 'block', height: 'auto' }} />
                    <div style={{
                      position: 'absolute', inset: 0, background: 'rgba(26,46,26,0)',
                      transition: 'background 0.3s', display: 'flex', alignItems: 'flex-end',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(26,46,26,0.45)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(26,46,26,0)'; }}
                    >
                      <div style={{ padding: '16px', opacity: 0, transition: 'opacity 0.3s' }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = 1; }}
                      >
                        <span style={{ color: 'var(--white)', fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem' }}>{item.title}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              background: 'rgba(0,0,0,0.92)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 40,
            }}
          >
            <motion.img
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              src={lightbox.url}
              alt={lightbox.title}
              style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 2 }}
              onClick={e => e.stopPropagation()}
            />
            <button onClick={() => setLightbox(null)} style={{
              position: 'absolute', top: 24, right: 24,
              background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
              color: 'white', width: 44, height: 44, borderRadius: '50%',
              fontSize: '1.2rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>✕</button>
            <div style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.7)', fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem', fontStyle: 'italic' }}>
              {lightbox.title}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </PageWrapper>
  );
}
